<template>
    <div>
        <breadcrumbs />
        <div class="mt-4">
            <div class="d-flex align-center">
                <span class="text-h6 font-weight-bold">
                    {{ $t('inbound_emails') }}
                </span>
                <v-btn class="ms-1" icon="mdi-reload" variant="text" @click="loadInboundEmails" />
            </div>
            <v-data-table :items="emails" :headers="headers" :loading="isLoadingEmails" disable-sort>
                <template v-slot:item.date="{item}">
                    {{ $filters.dateTimeFormat(new Date(item.date).toISOString()) }}
                </template>
                <template v-slot:item.link="{item}">
                    <v-btn icon="mdi-link-variant-plus" variant="text" @click="openCreateDialog(item)" />
                </template>
            </v-data-table>
            <ModalWrapper
                v-if="createCommunicationDialog"
                v-model="createCommunicationDialog"
                scrollable
                max-width="1300"
            >
                <template v-slot:content>
                    <communications
                        ref="communicationsDialog"
                        elevation="3"
                        is-hide-back-button
                        @send-create-communication="createCommunicationDialog = false"
                    />
                </template>
            </ModalWrapper>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import FeedbackList from '@/components/account/feedback/FeedbackList.vue'
    import Communications from '~/components/account/communications/Communications.vue'
    import {format} from 'date-fns'

    @Component({
        components: {
            Communications,
            FeedbackList,
            Breadcrumbs,
        },
    })
    export class InboundEmailsPage extends Vue {
        emails = []

        isLoadingEmails = false

        createCommunicationDialog = false

        async mounted() {
            await this.loadInboundEmails()
        }

        async loadInboundEmails() {
            try {
                this.isLoadingEmails = true
                this.emails = (await this.$api.inboundEmails.get())?.sort((a, b) => new Date(b.date) - new Date(a.date))
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoadingEmails = false
            }
        }

        get headers() {
            return [
                {title: this.$t('date'), value: 'date'},
                {title: this.$t('email_form.from'), value: 'from'},
                {title: this.$t('email_form.subject'), value: 'subject'},
                {title: '', value: 'link', align: 'center'},
            ]
        }

        async openCreateDialog(email) {
            this.createCommunicationDialog = true

            await this.$nextTick()

            this.$refs.communicationsDialog.create()

            const firstNumberRegex = /\d+/
            const orderId = email.subject?.match(firstNumberRegex)?.[0]

            this.$refs.communicationsDialog.communicationData.type = 'EMAIL'
            this.$refs.communicationsDialog.communicationData.stage = 'HELD'
            this.$refs.communicationsDialog.communicationData.communicationDateTime = format(
                new Date(email.date),
                "yyyy-MM-dd'T'HH:mm"
            )
            await this.$nextTick()

            if (orderId) {
                this.$refs.communicationsDialog.communicationData.orderId = orderId
            }
            if (email.messageText) {
                this.$refs.communicationsDialog.communicationData.description = email.messageText
            }
            if (email.subject) {
                this.$refs.communicationsDialog.communicationData.subject = email.subject
            }
            this.$refs.communicationsDialog.communicationData = {
                ...this.$refs.communicationsDialog.communicationData,
                recipientFromMails: email.from || [],
            }
            this.$refs.communicationsDialog.communicationData = {
                ...this.$refs.communicationsDialog.communicationData,
                recipientToMails: email.to || [],
            }
            this.$refs.communicationsDialog.communicationData = {
                ...this.$refs.communicationsDialog.communicationData,
                recipientCCMails: email.cc || [],
            }
            this.$refs.communicationsDialog.communicationData = {
                ...this.$refs.communicationsDialog.communicationData,
                recipientBCCMails: [],
            }
            if (this.$refs.communicationsDialog?.$refs?.form?.$refs?.accountAutocomplete) {
                const rqPClients = {
                    active: true,
                    email: email.from?.[0],
                }
                const companyRequests = ['CLIENT', 'SUPPLIER'].map(relationship => ({
                    order: 'ALPHABET',
                    active: true,
                    ...(relationship && {relationship}),
                    email: email.from?.[0],
                }))
                try {
                    this.$refs.communicationsDialog.$refs.form.$refs.accountAutocomplete.loading = true

                    const [{persons}, ...rs] = await Promise.all([
                        this.$api.privateClients.get(rqPClients),
                        ...companyRequests.map(rq => this.$api.companies.get(rq)),
                    ])
                    const accountItems = [
                        ...persons.map(p => ({...p, id: p.personId})),
                        ...rs.map(({companies}) => companies).flat(),
                    ]

                    if (accountItems?.length) {
                        this.$refs.communicationsDialog.$refs.form.$refs.accountAutocomplete.entries = accountItems

                        if (accountItems?.length === 1) {
                            this.$refs.communicationsDialog.$refs.form.$refs.accountAutocomplete.city = accountItems[0]
                        } else {
                            this.$toast.warning(this.$t('warning_message.several_accounts_email'))
                        }
                    }
                } finally {
                    this.$refs.communicationsDialog.$refs.form.$refs.accountAutocomplete.loading = false
                }
            }
        }
    }

    export default InboundEmailsPage
</script>
